import axios from "axios";
import React, { useRef, useState } from "react";

import useFeatureFlag from "../../../../app/hooks/useFeatureFlag";
import {
  trackCheckoutStep2Advanced,
  trackCheckoutStep2AdvancedFailed,
  trackSignUpGeneralInfoStepAdvanced,
} from "app/services/tracking/payments/checkoutStepsEvents";
import traceId from "../../../../redux/traceId";
import { FEATURE_FLAGS } from "../../../constants/FeatureFlag";
import getCookie from "../../../helpers/Cookies";
import { createHubspotContact } from "../../../modules/Utils/hubpspot/api";
import RightInfo from "../components/RightInfo/RightInfo";
import { tracking } from "../../../services/tracking";
import { useTrackCheckoutStarted } from "../../CadastroV2/useTrackCheckoutStarted";
import { identifyAnonymousTraits } from "../../../services/tracking/segmentIdentity";
import { sanitizeOnBlur } from "../../../helpers/sanitizeInput";
import OabPartnershipNotice from "../components/OabPartnershipNotice";
import { focusFirstInvalidField } from "../focusFirstInvalidField";

const sanitizeStep1Values = values => ({
  ...values,
  name: sanitizeOnBlur(values.name, "text"),
  email: sanitizeOnBlur(values.email, "email"),
  confirmation_email: sanitizeOnBlur(values.confirmation_email, "email"),
});

export default function Step1(props) {
  const { isFeatureFlagEnable, flagsReady } = useFeatureFlag(
    FEATURE_FLAGS.KILL_SWITCH.ENABLE_CHECKOUT_CUSTOMER_CREATE
  );

  const [isPersisting, setIsPersisting] = useState(false);
  const isPersistingRef = useRef(false);

  useTrackCheckoutStarted({
    formValues: props.formik.values,
    isCatalogSettled: props.isPricingSettled,
  });

  const redirectToNextStep = async () => {
    if (isPersistingRef.current) {
      return;
    }

    setIsPersisting(true);

    let values = props.formik.values;

    try {
      isPersistingRef.current = true;
      // O "Ir" do teclado envia o form sem passar pelo onBlur (que limpa os campos); sem isto o
      // 1º e-mail já está em minúsculas e o 2º ainda como digitado, e a comparação falha.
      values = sanitizeStep1Values(props.formik.values);
      props.formik.setValues(values, false);

      const validation = await props.formik.validateForm(values);
      props.formik.setTouched(validation);

      if (Object.keys(validation).length !== 0) {
        const firstErrorKey = Object.keys(validation)[0];
        const firstErrorMessage = validation[firstErrorKey];

        // No celular a mensagem pode estar fora da tela: rola até o primeiro campo com erro.
        focusFirstInvalidField(validation);

        const step2FailedProps =
          tracking.checkout.prepareCheckoutStep2AdvancedFailedForAnalytics(
            values,
            null,
            {
              type: "validation_error",
              message: firstErrorMessage,
              code: "VALIDATION_FAILED",
              validation_errors: validation,
              failed_field: firstErrorKey,
            }
          );
        trackCheckoutStep2AdvancedFailed(step2FailedProps);

        return;
      }

      const hutk = getCookie("hubspotutk");

      if (process.env.NODE_ENV === "production") {
        createHubspotContact(
          { ...props, formik: { ...props.formik, values } },
          hutk,
          "App"
        );
      }

      const traits = {
        name: values.name,
        email: values.email,
      };

      if (flagsReady && isFeatureFlagEnable) {
        const response = await axios.post(
          process.env.REACT_APP_JUSBILL_URL + "/clients/persist",
          {
            name: values.name,
            email: values.email,
          },
          {
            headers: {
              "x-trace-id": traceId,
            },
          }
        );

        props.formik.setFieldValue("createdCustomerId", response?.data?.id);
      }

      props.setStep(1);
      identifyAnonymousTraits(traits);
      trackSignUpGeneralInfoStepAdvanced();

      const step2AdvancedProps =
        tracking.checkout.prepareCheckoutStep2AdvancedForAnalytics(
          values,
          {}
        );
      trackCheckoutStep2Advanced(step2AdvancedProps);
    } catch (e) {
      const step2FailedProps =
        tracking.checkout.prepareCheckoutStep2AdvancedFailedForAnalytics(
          values,
          null,
          {
            type: "error_persisting_customer",
            message:
              e.response?.data?.message ||
              e.message ||
              "Erro ao avançar para o step 2 - falha ao criar usuário",
            code: e.response?.status || "unknown",
          }
        );
      trackCheckoutStep2AdvancedFailed(step2FailedProps);
    } finally {
      setIsPersisting(false);
      isPersistingRef.current = false;
    }
  };

  // noValidate: as mensagens vêm do Formik; sem isso o type="email" abriria o balão nativo por cima.
  const handleSubmit = event => {
    event.preventDefault();
    redirectToNextStep();
  };

  return (
    <div className="row no-gutters mb-20">
      <div className="col-xl-8">
        <form
          className="border-radius-only-left"
          style={{ backgroundColor: "#fff", padding: "40px", height: "100%" }}
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="row">
            <div className="col-xl-4 d-flex align-items-center mb-10">
              <div>
                <div className={`step ${props.step == 0 ? "active" : ""}`}>
                  <span>1</span>
                </div>
              </div>
              <div className="step-info d-flex flex-column">
                <span className="step-title">Passo 1 - Cadastro</span>
              </div>
            </div>
            <div className="col-xl-4 d-flex align-items-center mb-10">
              <div>
                <div className={`step ${props.step == 1 ? "active" : ""}`}>
                  <span>2</span>
                </div>
              </div>
              <div className="step-info d-flex flex-column">
                <span className="step-title">Passo 2 - Pagamento</span>
              </div>
            </div>
          </div>

          <div className="row mb-5">
            <div className="col-xl-4"></div>
          </div>

          <div className="row mb-5">
            <div className="col-xl-12">
                <p
                  className="font-size-h3 font-weight-normal font-weight-bolder mb-0"
                >
                  Crie seu acesso para finalizar sua compra
                </p>
                <p
                  className="text-dark-50 font-weight-normal font-size-lg mb-5"
                >
                  Na próxima etapa, você escolherá a forma de pagamento
                </p>
            </div>
          </div>

          <OabPartnershipNotice notice={props.oabPartnershipNotice} />

          <div className="row mb-5">
            <div className="col-xl-12">
              <label className="form-label" htmlFor="checkout-name">
                Nome completo <span className="text-danger">&nbsp;*</span>
              </label>
              <input
                id="checkout-name"
                type="text"
                name="name"
                autoComplete="name"
                autoCapitalize="words"
                className={
                  "form-control " +
                  (props.formik.errors.name && props.formik.touched.name
                    ? "is-invalid"
                    : "")
                }
                value={props.formik.values.name}
                onChange={props.formik.handleChange}
                onBlur={e => {
                  const sanitizedValue = sanitizeOnBlur(e.target.value, "text");
                  props.formik.setFieldValue("name", sanitizedValue);
                }}
                maxLength={64}
              />
              {props.formik.errors.name && props.formik.touched.name && (
                <label className="invalid-feedback">
                  {props.formik.errors.name}
                </label>
              )}
            </div>
          </div>

          <div className="row mb-5">
            <div className="col-xl-6">
              <label className="form-label" htmlFor="checkout-email">
                E-mail <span className="text-danger">&nbsp;*</span>
              </label>
              <input
                id="checkout-email"
                type="email"
                name="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className={
                  "form-control " +
                  (props.formik.errors.email && props.formik.touched.email
                    ? "is-invalid"
                    : "")
                }
                value={props.formik.values.email}
                onChange={props.formik.handleChange}
                onBlur={e => {
                  const sanitizedValue = sanitizeOnBlur(
                    e.target.value,
                    "email"
                  );
                  props.formik.setFieldValue("email", sanitizedValue);
                  props.formik.setFieldTouched("email", true, false);
                }}
                aria-invalid={Boolean(
                  props.formik.errors.email && props.formik.touched.email
                )}
                aria-describedby="checkout-email-error"
                maxLength={64}
              />
              {props.formik.errors.email && props.formik.touched.email && (
                <label
                  id="checkout-email-error"
                  className="invalid-feedback"
                  role="alert"
                >
                  {props.isUniqueEmail ? (
                    <span>
                      E-mail já cadastrado. Para fazer o login,&nbsp;
                      <a
                        rel="noreferrer noopener"
                        href="/login"
                        style={{
                          background: "#41c78f",
                          color: "transparent",
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                        }}
                      >
                        clique aqui.
                      </a>
                    </span>
                  ) : (
                    props.formik.errors.email
                  )}
                </label>
              )}
            </div>
            <div className="col-xl-6">
              <label className="form-label" htmlFor="checkout-confirmation-email">
                Confirmar e-mail <span className="text-danger">&nbsp;*</span>
              </label>
              <input
                id="checkout-confirmation-email"
                type="email"
                name="confirmation_email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className={
                  "form-control " +
                  (props.formik.errors.confirmation_email &&
                  props.formik.touched.confirmation_email
                    ? "is-invalid"
                    : "")
                }
                value={props.formik.values.confirmation_email}
                onChange={props.formik.handleChange}
                onBlur={e => {
                  const sanitizedValue = sanitizeOnBlur(
                    e.target.value,
                    "email"
                  );
                  props.formik.setFieldValue(
                    "confirmation_email",
                    sanitizedValue
                  );
                  props.formik.setFieldTouched("confirmation_email", true, false);
                }}
                aria-invalid={Boolean(
                  props.formik.errors.confirmation_email &&
                    props.formik.touched.confirmation_email
                )}
                aria-describedby="checkout-confirmation-email-error"
                maxLength={64}
              />
              {props.formik.errors.confirmation_email &&
                props.formik.touched.confirmation_email && (
                  <label
                    id="checkout-confirmation-email-error"
                    className="invalid-feedback"
                    role="alert"
                  >
                    {props.formik.errors.confirmation_email}
                  </label>
                )}
            </div>
          </div>

          <div className="row mb-5">
            <div className="col-xl-12">
              <button
                type="submit"
                name="advance_button"
                disabled={isPersisting}
                className="btn btn-primary"
                style={{
                  width: "100%",
                  height: "50px",
                  marginTop: "calc(42px - 1.25rem)",
                }}
              >
                CONTINUAR PARA PAGAMENTO
              </button>
            </div>
          </div>
        </form>
      </div>
      <RightInfo
        formik={props.formik}
        disabledCreditCard={props.disabledCreditCard}
      />
    </div>
  );
}
